/**
 * Branding Settings Tab Component
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Image, Settings } from 'lucide-react';
import { ThemeSettings } from './types';
import { BrandingImageTile } from './BrandingImageTile';

interface BrandingTabProps {
  themeSettings: ThemeSettings;
  saving: boolean;
  onThemeSettingsChange: (settings: ThemeSettings) => void;
  onSave: () => void;
  onResetColors: () => void;
  onFileUpload: (fileType: string) => void;
  /** CR-6243: stored URL per tile key, so each tile previews what is applied. */
  brandingImages?: Record<string, string | null>;
  /** CR-6243: which tiles have a file in flight. */
  uploadingFiles?: Record<string, boolean>;
}

interface ColorFieldProps {
  id: string;
  label: string;
  hint: string;
  value: string;
  onChange: (value: string) => void;
}

/** A colour swatch with its hex value beside it. */
function ColorField({ id, label, hint, value, onChange }: ColorFieldProps) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex gap-2">
        <Input
          type="color"
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 w-20"
        />
        <Input
          type="text"
          aria-label={`${label} hex value`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1"
        />
      </div>
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

export function BrandingTab({
  themeSettings,
  saving,
  onThemeSettingsChange,
  onSave,
  onResetColors,
  onFileUpload,
  brandingImages = {},
  uploadingFiles = {},
}: BrandingTabProps) {
  // CR-6245: the login and button colours are one setting. Every update
  // saves them as one value, so older branding with two different values is
  // brought into line once any field here is edited and saved; until then
  // the portal still shows one colour, the button colour.
  const updateTheme = (updates: Partial<ThemeSettings>) => {
    const next = { ...themeSettings, ...updates };
    onThemeSettingsChange({ ...next, loginColor: next.buttonColor });
  };

  return (
    <div className="space-y-4">
      {/* Page Customization */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Image className="h-5 w-5" />
            Page Customization
          </CardTitle>
          <CardDescription>
            Customize logos, colors, and images
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Sign-in page heading — editable override for the default "<Platform Name> Portal" */}
          <div className="space-y-2">
            <Label htmlFor="loginTitle">Sign-in Page Heading</Label>
            <Input
              id="loginTitle"
              type="text"
              value={themeSettings.loginTitle}
              onChange={(e) => updateTheme({ loginTitle: e.target.value })}
              placeholder="e.g. Your Supplier Portal"
            />
            <p className="text-xs text-muted-foreground">
              Shown below the logo on the sign-in page. Leave blank to use
              "<span className="font-mono">{'{Platform Name}'} Portal</span>".
            </p>
          </div>

          {/* CR-6243: every tile previews the image that is actually applied,
              so an admin can see at a glance what the portal is using and
              whether an upload landed.

              The hints say exactly WHERE each image appears. They used to be
              aspirational ("wherever the portal renders on a dark
              background"), which sent admins hunting for a dark logo that,
              with a header logo set, has nowhere to show. Keep these honest:
              if the placement rules change, change the copy with them. */}
          <div className="grid md:grid-cols-2 gap-6 pt-4 border-t">
            <BrandingImageTile
              fileType="companyLogo"
              label="Company Light Logo"
              hint="Shown on the sign-in page, and in the sidebar when no header logo is set"
              imageUrl={brandingImages['companyLogo'] ?? null}
              uploading={!!uploadingFiles['companyLogo']}
              onUpload={onFileUpload}
            />
            <BrandingImageTile
              fileType="companyDarkLogo"
              label="Company Dark Logo"
              hint="Shown in the sidebar in dark mode, and only when no header logo is set"
              imageUrl={brandingImages['companyDarkLogo'] ?? null}
              uploading={!!uploadingFiles['companyDarkLogo']}
              onUpload={onFileUpload}
            />
            <BrandingImageTile
              fileType="loginImage"
              label="Login Background Image"
              hint="Fills the sign-in, forgotten-password and reset-password pages behind the form"
              imageUrl={brandingImages['loginImage'] ?? null}
              uploading={!!uploadingFiles['loginImage']}
              onUpload={onFileUpload}
              transparent={false}
            />
            <BrandingImageTile
              fileType="headerLogo"
              label="Header Company Logo"
              hint="Shown in the sidebar header in both light and dark mode, in place of the company logo"
              imageUrl={brandingImages['headerLogo'] ?? null}
              uploading={!!uploadingFiles['headerLogo']}
              onUpload={onFileUpload}
            />
          </div>

          {/* Color Pickers. CR-6245: the page background and body text are
              system-defined, so neither is offered here. */}
          <div className="grid md:grid-cols-2 gap-4 pt-4 border-t">
            <ColorField
              id="buttonColor"
              label="Login & Button Color"
              hint="One color for the sign-in page accent and every button"
              value={themeSettings.buttonColor}
              onChange={(value) => updateTheme({ buttonColor: value })}
            />
            <ColorField
              id="fontColor"
              label="Heading Font Color"
              hint="Page titles and main headings in light mode; all other text keeps the system color"
              value={themeSettings.fontColor}
              onChange={(value) => updateTheme({ fontColor: value })}
            />
          </div>

          <div className="flex gap-3">
            <Button onClick={onSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Page Settings'}
            </Button>
            <Button variant="outline" disabled={saving} onClick={onResetColors}>
              {saving ? 'Resetting...' : 'Reset Colors to Default'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Main Company Details */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Main Company Details
          </CardTitle>
          <CardDescription>
            Company information and contact details
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="companyName">Platform Name</Label>
            <Input id="companyName" value={themeSettings.companyName} onChange={(e) => updateTheme({ companyName: e.target.value })} placeholder="Helix" />
            <p className="text-sm text-muted-foreground">This name appears in the header, login page, and email communications</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="contactEmail">Contact Email</Label>
            <Input id="contactEmail" type="email" value={themeSettings.contactEmail} onChange={(e) => updateTheme({ contactEmail: e.target.value })} placeholder="contact@company.com" />
            <p className="text-sm text-muted-foreground">Main contact email for the organisation</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="companyPhone">Phone Number</Label>
            <Input id="companyPhone" type="tel" value={themeSettings.companyPhone} onChange={(e) => updateTheme({ companyPhone: e.target.value })} placeholder="+44 123 456 7890" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="companyAddress">Address</Label>
            <Input id="companyAddress" value={themeSettings.companyAddress} onChange={(e) => updateTheme({ companyAddress: e.target.value })} placeholder="Company address" />
          </div>
          <Button onClick={onSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Company Details'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
